/**
 * GuardianTransaction types for tracking async proposal execution.
 *
 * These types are shared between:
 * - Sync server: GET /guardian-transactions/:id endpoint response
 * - UI web: useGuardianTransactionStatus hook polling
 *
 * @module guardianTransaction
 */

/**
 * Status values for GuardianTransaction lifecycle.
 * Maps to the GuardianTransactionStatus enum in the database.
 *
 * Flow: QUEUED -> VALIDATING -> SIGNING -> EXECUTING -> APPLYING_CHANGES -> COMPLETED
 * Or at any point: -> FAILED / REVERTED / BLOCKED / PARTIALLY_EXECUTED
 */
export type GuardianTransactionStatus =
  | 'QUEUED'
  | 'VALIDATING'
  | 'SIGNING'
  | 'EXECUTING'
  | 'APPLYING_CHANGES'
  | 'COMPLETED'
  | 'PARTIALLY_EXECUTED'
  | 'FAILED'
  | 'REVERTED'
  | 'BLOCKED';

/**
 * Proposal types in the system.
 * Maps to the ProposalType enum in the database.
 *
 * This shared value/type pair keeps API and UI code from importing Prisma
 * enums while still using the same serialized proposal-family strings.
 */
export const ProposalTypeValue = {
  ORGANIZATION_MEMBER: 'ORGANIZATION_MEMBER',
  ORGANIZATION_ADMIN: 'ORGANIZATION_ADMIN',
  ACCOUNT_TRANSACTION: 'ACCOUNT_TRANSACTION',
  MULTICHAIN_ACCOUNT: 'MULTICHAIN_ACCOUNT',
  GROUP: 'GROUP',
  POLICY: 'POLICY',
  DISASTER_RECOVERY: 'DISASTER_RECOVERY',
} as const;

export type ProposalType =
  (typeof ProposalTypeValue)[keyof typeof ProposalTypeValue];

/**
 * Proposal families that execute as organization operations. Account
 * transactions intentionally stay outside this subset.
 */
export type OrgOperationProposalType = Exclude<
  ProposalType,
  typeof ProposalTypeValue.ACCOUNT_TRANSACTION
>;

/**
 * Response type for GET /guardian-transactions/:id endpoint.
 * Used by useGuardianTransactionStatus hook for polling execution status.
 *
 * @example
 * ```typescript
 * // In UI component
 * const { data } = useGuardianTransactionStatus(guardianTransactionId);
 * if (data?.status === 'COMPLETED') {
 *   // Show success state
 * } else if (data?.status === 'FAILED') {
 *   // Show error with data.errorMessage
 * }
 * ```
 */
export interface GuardianTransactionResponse {
  /** Unique identifier for the guardian transaction */
  id: string;

  /** Type of proposal that triggered this transaction */
  proposalType: ProposalType;

  /** ID of the proposal being executed */
  proposalId: string;

  /** Current execution status */
  status: GuardianTransactionStatus;

  /** Human-readable error message if status is FAILED/REVERTED */
  errorMessage: string | null;

  /** ID of the record created by this transaction (e.g., new groupId) */
  createdRecordId: string | null;

  /** When this transaction was created (ISO 8601 string) */
  createdAt: string;

  /** When this transaction was last updated (ISO 8601 string) */
  updatedAt: string;
}

/**
 * Helper to check if a status indicates the transaction is still in progress.
 *
 * @param status - The current GuardianTransaction status
 * @returns true if the transaction is still processing
 */
export function isGuardianTransactionInProgress(
  status: GuardianTransactionStatus
): boolean {
  return (
    status === 'QUEUED' ||
    status === 'VALIDATING' ||
    status === 'SIGNING' ||
    status === 'EXECUTING' ||
    status === 'APPLYING_CHANGES'
  );
}

/**
 * Helper to check if a status indicates the transaction completed (success or failure).
 *
 * @param status - The current GuardianTransaction status
 * @returns true if the transaction has reached a terminal state
 */
export function isGuardianTransactionFinished(
  status: GuardianTransactionStatus
): boolean {
  return (
    status === 'COMPLETED' ||
    status === 'PARTIALLY_EXECUTED' ||
    status === 'FAILED' ||
    status === 'REVERTED' ||
    status === 'BLOCKED'
  );
}

/**
 * Human-readable status messages for UI display.
 */
export const GUARDIAN_TRANSACTION_STATUS_MESSAGES: Record<
  GuardianTransactionStatus,
  string
> = {
  QUEUED: 'Queued for execution...',
  VALIDATING: 'Validating proposal...',
  SIGNING: 'Guardian signing...',
  EXECUTING: 'Executing onchain...',
  APPLYING_CHANGES: 'Applying changes...',
  COMPLETED: 'Completed successfully',
  PARTIALLY_EXECUTED:
    'Please contact Den support as this transaction is partially executed.',
  FAILED: 'Execution failed',
  REVERTED: 'Transaction reverted',
  BLOCKED: 'Blocked',
};

/**
 * Sentinel stored as errorMessage on a GuardianTransaction when execution is
 * blocked because the estimated gas exceeds the platform ceiling.
 * Used by the UI to detect this specific reason and display a friendly message.
 */
export const GAS_LIMIT_EXCEEDED_ERROR_CODE = 'BLOCKED_GAS_TOO_HIGH';

/**
 * User-facing message shown in the execution status modal when
 * errorMessage === GAS_LIMIT_EXCEEDED_ERROR_CODE.
 */
export const GAS_LIMIT_EXCEEDED_USER_MESSAGE =
  'This transaction cannot be executed because the estimated gas used is too high. ' +
  'Contact the Den team if you believe this is a mistake or you want to increase the limit.';
